import {
  AccountsApi,
  ApiApi,
  ApiJwtTokenApi,
  AuxMetricsExcelExportApi,
  BaseAPI,
  Configuration,
  CustomQuestionnaireApi,
  DashboardApi,
  DatasetPiplineApi,
  DatasetsApi,
  GenerationInputsApi,
  InferencersApi,
  QuestionnaireApi,
  RiskApi,
  RiskProfileApi,
} from '@api';
import { Apis } from '@data-access';

import { CookieHelper } from './cookie';
import { dedupedFetch } from './requestDedupe';

const createApiConfiguration = async (accessToken?: string): Promise<Configuration> => {
  const basePath =
    process.env.NODE_ENV === 'development' ? process.env.NEXT_PUBLIC_API_BASE_PATH : window.location.origin;

  const res = await dedupedFetch(`${basePath}/accounts/csrf/`);

  const csrfToken = res.headers.get('X-csrftoken');
  CookieHelper.set('csrftoken', csrfToken || '');
  if (!csrfToken) {
    throw new Error('Requesting CSRF token failed: Header was missing');
  }

  const token = accessToken || CookieHelper.get('access_token');

  return new Configuration({
    basePath,
    headers: {
      'X-CSRFToken': csrfToken,
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    fetchApi: dedupedFetch,
  });
};

const createApisFromConfig = (config: Configuration): Apis => ({
  api: new ApiApi(config),
  accounts: new AccountsApi(config),
  auth: new ApiJwtTokenApi(config),
  customQuestionnaireApi: new CustomQuestionnaireApi(config),
  riskApi: new RiskApi(config),
  riskProfileApi: new RiskProfileApi(config),
  dashboardApi: new DashboardApi(config),
  generationInputsApi: new GenerationInputsApi(config),
  datasetPiplineApi: new DatasetPiplineApi(config),
  datasetsApi: new DatasetsApi(config),
  inferencersApi: new InferencersApi(config),
  questionnaireApi: new QuestionnaireApi(config),
  auxMetricsExcelExportApi: new AuxMetricsExcelExportApi(config),
});

export const APIHelper = {
  createApiConfiguration,
  createApisFromConfig,
};

export const withQueryParams = <T extends BaseAPI>(api: T, queryParams: Record<string, string | string[]>): T =>
  api.withPreMiddleware(async ({ url, init }) => {
    const nextUrl = new URL(url);

    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(v => nextUrl.searchParams.append(key, v));
      } else {
        nextUrl.searchParams.set(key, value);
      }
    });

    return {
      url: nextUrl.toString(),
      init,
    };
  }) as T;
